import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiMail, FiShield, FiLock, FiEdit2, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { mockApi } from '../api/mockApi';

const VerificationBlock = ({ formType, categoryId, onVerifyStatusChange }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); 
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  
  const [sessionData, setSessionData] = useState({
    verificationId: null,
    sessionToken: null,
    isVerified: false
  });

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (step !== 'email') {
      handleReset();
    }
  }, [email]);

  const handleSendCode = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await mockApi.sendVerificationCode(email, formType, categoryId);
      
      const newSession = {
        verificationId: res.verificationId,
        sessionToken: res.sessionToken || null,
        isVerified: false
      };
      
      setSessionData(newSession);
      onVerifyStatusChange({
        ...newSession,
        email: email,
        expiresAt: res.expiresAt
      });

      setStep('code');
      setResendTimer(30);
    } catch (err) {
      setError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || loading) return;
    
    setLoading(true);
    setError('');
    try {
      const res = await mockApi.resendCode(email, sessionData.verificationId);
      
      const updatedSession = {
        ...sessionData,
        verificationId: res.verificationId || sessionData.verificationId,
        sessionToken: res.sessionToken || sessionData.sessionToken
      };

      setSessionData(updatedSession);
      onVerifyStatusChange({
        ...updatedSession,
        email: email,
        expiresAt: res.expiresAt
      });
      
      setResendTimer(30);
    } catch (err) {
      setError(err.message || 'Resend failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('Code must be exactly 6 digits.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await mockApi.verifyCode(email, code, sessionData.verificationId);
      
      const finalToken = res.sessionToken || sessionData.sessionToken;
      const verifiedState = {
        verificationId: sessionData.verificationId,
        sessionToken: finalToken,
        isVerified: true
      };

      setSessionData(verifiedState);
      setStep('verified');
      
      onVerifyStatusChange({
        ...verifiedState,
        email: email,
        verifiedEmail: email,
        verifiedAt: res.verifiedAt || new Date().toISOString(),
        expiresAt: res.expiresAt
      });
    } catch (err) {
      setError(err.message || 'Verification failed. Please check the code.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('email');
    setCode('');
    setError('');
    setResendTimer(0);
    setSessionData({ verificationId: null, sessionToken: null, isVerified: false });
    
    onVerifyStatusChange({
      isVerified: false,
      email: '',
      verifiedEmail: '',
      verificationId: null,
      sessionToken: null,
      verifiedAt: null,
      expiresAt: null
    });
  };

  const isActuallyVerified = step === 'verified' && 
                              sessionData.isVerified && 
                              sessionData.verificationId && 
                              sessionData.sessionToken;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <SafeIcon icon={FiShield} className="text-blue-600 text-xl" />
        <h3 className="font-semibold text-slate-800">Email Verification</h3>
      </div>

      <AnimatePresence mode="wait">
        {step === 'email' && (
          <motion.div key="email-step" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <label className="block text-sm font-medium text-slate-700 mb-1">Business Email</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-slate-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="name@company.com"
                  disabled={loading}
                />
              </div>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={loading || !email}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 min-w-[140px]"
              >
                {loading ? 'Sending...' : 'Request Code'}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'code' && (
          <motion.div key="code-step" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-slate-700">6-Digit Code</label>
              <button type="button" onClick={handleReset} className="text-xs text-blue-600 hover:underline">Change Email</button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="text-slate-400" />
                </div>
                <input
                  type="text"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="pl-10 w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all tracking-[0.5em] font-mono text-center"
                  placeholder="000000"
                  disabled={loading}
                />
              </div>
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={loading || code.length !== 6}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 min-w-[140px]"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
            <div className="mt-3 flex justify-center">
              <button 
                type="button" 
                onClick={handleResendCode}
                disabled={resendTimer > 0 || loading}
                className="text-xs flex items-center gap-1.5 transition-colors font-medium disabled:text-slate-400 text-blue-600 hover:text-blue-700"
              >
                <SafeIcon icon={FiRefreshCw} className={loading && step === 'code' ? 'animate-spin' : ''} />
                {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Code expired or not received? Resend code.'}
              </button>
            </div>
          </motion.div>
        )}

        {isActuallyVerified && (
          <motion.div key="verified-step" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiCheckCircle} className="text-green-600 text-2xl" />
              <div>
                <p className="text-green-800 font-bold text-sm uppercase tracking-wide">Verified</p>
                <p className="text-green-600 text-xs">{email}</p>
              </div>
            </div>
            <button type="button" onClick={handleReset} className="text-slate-400 hover:text-slate-600 p-2 rounded-full transition-colors">
              <SafeIcon icon={FiEdit2} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mt-3 text-red-500 text-sm">
          <SafeIcon icon={FiAlertCircle} />
          <span>{error}</span>
        </motion.div>
      )}
    </div>
  );
};

export default VerificationBlock;